import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import { CorrosionRateService } from 'src/app/modules/CorrosionRate/services/corrosion-rate.service';
import { CorrosionclasService } from 'src/app/services/Corrosionclas.service';
import { DataService } from 'src/app/services/Data.service';
import { MatSelGuidelinesService } from 'src/app/services/mat-sel-guidelines/mat-sel-guidelines.service';
import { PerforatedPipeService } from 'src/app/services/perforated-pipe/perforated-pipe.service';
import { SlottedPipeService } from 'src/app/services/slotted-pipe/slotted-pipe.service';
import { TreeService } from 'src/app/services/Tree.service';
import { PreSimulationService } from '../../pre-simulation/service/pre-simulation.service';
import { SaveSimulationService } from './save-simulation.service';
import { HeaderActionType, HeaderActionsService } from 'src/app/services/header-actions.service';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SimulationVisibility } from 'src/app/shared/enums/simulation-visibility.enum';

// --- Interfaces ---
interface Entity {
    id: number | null;
    name: string;
    __isNew__?: boolean;
}

export interface SimulationNodeData {
    userId: number | null;
    customer: { id: number | null; name: string };
    region: { id: number | null; name: string };
    project: { id: number | null; name: string };
    simulation: { id: number | null; name: string };
}

export interface SaveSimulationForm {
    userId: number | null;
    customer: {
        id: number | null;
        description: string;
    };
    region: {
        id: number | null;
        description: string;
    };
    project: {
        id: number | null;
        description: string;
    };
    simulation: {
        id: number | null;
        description: string;
    };
    visibility: string;
    notes: string;
    path: string;
}

// --- Helper Functions ---
export function toSimulationNodeData(form: SaveSimulationForm): SimulationNodeData {
    return {
        userId: form.userId,
        customer: { id: form.customer?.id ?? null, name: form.customer?.description ?? '' },
        region: { id: form.region?.id ?? null, name: form.region?.description ?? '' },
        project: { id: form.project?.id ?? null, name: form.project?.description ?? '' },
        simulation: { id: form.simulation?.id ?? null, name: form.simulation?.description ?? '' }
    };
}

export function appendSimulationNodeDataToFormData(formData: FormData, saveSimulationForm: SaveSimulationForm): void {
    const simulationNodeData = toSimulationNodeData(saveSimulationForm);
    formData.append("simulationNodeData.userId", String(simulationNodeData.userId ?? ''));
    formData.append("simulationNodeData.customer.id", String(simulationNodeData.customer?.id ?? ''));
    formData.append("simulationNodeData.customer.name", simulationNodeData.customer?.name ?? '');
    formData.append("simulationNodeData.region.id", String(simulationNodeData.region?.id ?? ''));
    formData.append("simulationNodeData.region.name", simulationNodeData.region?.name ?? '');
    formData.append("simulationNodeData.project.id", String(simulationNodeData.project?.id ?? ''));
    formData.append("simulationNodeData.project.name", simulationNodeData.project?.name ?? '');
    formData.append("simulationNodeData.simulation.id", String(simulationNodeData.simulation?.id ?? ''));
    formData.append("simulationNodeData.simulation.name", simulationNodeData.simulation?.name ?? '');
}

@Component({
    selector: 'app-save-simulation-modal',
    templateUrl: './save-simulation-modal.component.html',
    styleUrls: ['./save-simulation-modal.component.css']
})
export class SaveSimulationModalComponent implements OnInit {
    // --- Inputs ---
    @Input() module: string;
    @Input() userId: number;

    // --- Outputs ---
    @Output() close = new EventEmitter<void>();

    // --- Public properties ---
    form: UntypedFormGroup;
    filteredCustomers: Entity[] = [];
    filteredRegions: Entity[] = [];
    filteredProjects: Entity[] = [];
    filteredSimulations: Entity[] = [];
    users: Entity[] = [];
    customers: Entity[] = [];
    regions: Entity[] = [];
    projects: Entity[] = [];
    simulations: Entity[] = [];
    countries: Entity[] = [];
    selectedCountry: any;
    selectedCustomer: any;
    visibilityOptions = Object.values(SimulationVisibility).map(description => ({ description }));
    regionsLength: number;

    regionsLoaded$ = new BehaviorSubject<boolean>(false);
    projectsLoaded$ = new BehaviorSubject<boolean>(false);
    simulationsLoaded$ = new BehaviorSubject<boolean>(false);

    // --- Private properties ---
    private nodes: any = null;
    private simulationNameDraft: string | null = null;
    private simulationNameSource: Entity | null = null;

    // --- Tag creation functions (public for template usage) ---
    addCustomerTag = (name: string) => ({ id: null, name: name, __isNew__: true });
    addProjectTag = (name: string) => ({ id: null, name: name, __isNew__: true });
    addSimulationTag = (name: string) => ({ id: null, name: name, __isNew__: true });

    // --- Constructor ---
    constructor(
        public corrosionService: CorrosionclasService,
        public corrosionRateService: CorrosionRateService,
        public slottedPipeService: SlottedPipeService,
        public perforatedPipeService: PerforatedPipeService,
        public matSelGuidelinesService: MatSelGuidelinesService,
        public treeService: TreeService,
        public dataService: DataService,
        public preSimulationService: PreSimulationService,
        public saveSimulationService: SaveSimulationService,
        private fb: UntypedFormBuilder,
        private headerActionsService: HeaderActionsService
    ) { }

    // --- Lifecycle hooks ---
    ngOnInit() {
        this.selectedCustomer = this.preSimulationService.lastForm?.get('customer')?.value;
        this.selectedCountry = this.preSimulationService.lastForm?.get('country')?.value;
        this.userId = this.dataService.getCurrentUserId();
        this.module = this.dataService.getCurrentModule();

        this.form = this.fb.group({
            customer: [null, Validators.required],
            region: [null, Validators.required],
            project: [null, Validators.required],
            simulation: [null, [
                Validators.required,
                control => control.value?.__isNew__ && this.findByName(this.filteredSimulations, control.value)
                    ? { duplicateName: true }
                    : null
            ]],
            visibility: [{ value: null, disabled: true }, Validators.required],
            notes: [{ value: null, disabled: true }]
        });

        this.loadSelects();
        this.loadCountries();
    }

    waitForLoaded$(obs$: BehaviorSubject<boolean>) {
        return obs$.pipe(
            filter(loaded => !!loaded),
            take(1)
        ).toPromise();
    }

    async preloadForm() {
        // Last saved data
        const savedCustomerId = this.saveSimulationService.getSavedCustomerId();
        const savedCustomerName = this.saveSimulationService.getSavedCustomerName();
        const savedRegionId = this.saveSimulationService.getSavedRegionId();
        const savedRegionName = this.saveSimulationService.getSavedRegionName();
        const savedProjectId = this.saveSimulationService.getSavedProjectId();
        const savedProjectName = this.saveSimulationService.getSavedProjectName();
        const savedSimulationId = this.saveSimulationService.getSavedSimulationId();
        const savedSimulationName = this.saveSimulationService.getSavedSimulationName();

        // Customer
        const initialCustomer = (savedCustomerId && this.filteredCustomers.find(c => c.id === savedCustomerId))
            || (savedCustomerName && this.findByName(this.filteredCustomers, { name: savedCustomerName }))
            || (this.selectedCustomer && this.findByName(this.filteredCustomers, this.selectedCustomer));
        if (initialCustomer) {
            this.form.get('customer')?.setValue(initialCustomer);
            await this.waitForLoaded$(this.regionsLoaded$);
        } else if (this.selectedCustomer) {
            // If customer is not in options, add it and select
            // Only the customer field can be added as a new option, the others must all exist in the corresponding lists
            this.initFormControlWithFilteredList('customer', this.selectedCustomer, this.filteredCustomers);
        }

        // Region
        const initialRegion = (savedRegionId && this.filteredRegions.find(r => r.id === savedRegionId))
            || (savedRegionName && this.findByName(this.filteredRegions, { name: savedRegionName }))
            || (this.selectedCountry && this.findByName(this.filteredRegions, this.selectedCountry));

        if (!initialRegion) {
            return;
        }

        this.form.get('region')?.setValue(initialRegion);
        await this.waitForLoaded$(this.projectsLoaded$);

        // Project
        const initialProject = (savedProjectId && this.filteredProjects.find(p => p.id === savedProjectId))
            || (savedProjectName && this.findByName(this.filteredProjects, { name: savedProjectName }))
            || null;


        if (!initialProject) {
            return;
        }

        this.form.get('project')?.setValue(initialProject);
        await this.waitForLoaded$(this.simulationsLoaded$);


        // Simulation
        const initialSimulation = (savedSimulationId && this.filteredSimulations.find(s => s.id === savedSimulationId))
            || (savedSimulationName && this.findByName(this.filteredSimulations, { name: savedSimulationName }))
            || null;

        if (!initialSimulation) {
            return;
        }

        this.form.get('simulation')?.setValue(initialSimulation);

        // Visibility
        const savedVisibility = this.saveSimulationService.getSavedVisibility();

        if (savedVisibility) {
            const visOption = this.visibilityOptions.find(v => v.description === savedVisibility);
            this.form.get('visibility')?.setValue(visOption ?? { description: savedVisibility });
        }

        // Notes
        const savedNotes = this.saveSimulationService.getSavedNotes();

        if (savedNotes) {
            this.form.get('notes')?.enable();
            this.form.get('notes')?.setValue(savedNotes);
        }
    }

    // --- Public methods ---
    onClose(): void {
        this.close.emit();
    }

    saveSimulation(): void {
        this.commitSimulationName();
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.headerActionsService.requestAction(HeaderActionType.Save, this.mapFormValueToSaveSimulationForm(this.form.value));

        this.onClose();
    }

    get isNewSimulation(): boolean {
        if (this.simulationNameDraft !== null) {
            const name = this.normalizeName(this.simulationNameDraft);
            return !!name && name !== this.normalizeName(this.simulationNameSource?.name);
        }
        return !!this.form.get('simulation')?.value?.__isNew__;
    }

    onSimulationNameSearch(event: { term: string }): void {
        if (this.simulationNameDraft === null && !this.simulationNameSource) {
            this.simulationNameSource = this.form.get('simulation')?.value ?? null;
        }
        this.simulationNameDraft = event.term;
    }

    commitSimulationName(): void {
        if (this.simulationNameDraft === null) {
            return;
        }

        const control = this.form.get('simulation');
        const name = this.normalizeName(this.simulationNameDraft);
        const source = this.simulationNameSource;
        const simulation = name
            ? (source && this.normalizeName(source.name) === name ? source : this.createNewEntity(name))
            : null;

        // Commit before ng-select restores its label on blur, without reloading notes and visibility.
        control?.setValue(simulation, { emitEvent: false });
        control?.markAsTouched();
        if (simulation && this.form.get('visibility')?.disabled) {
            this.form.get('notes')?.enable();
            this.form.get('visibility')?.enable();
            this.getSimulation(simulation);
        }
        this.simulationNameDraft = null;
    }

    onRegionChange(selected: any) {
        if (selected && selected.isSeparator) {
            setTimeout(() => {
                this.form.get('region')?.setValue(null, { emitEvent: false });
            });
        }
    }

    // --- Private methods (helpers, internal logic)
    private configureSelects() {
        // Map customers with normalization
        this.filteredCustomers = this.nodes
            .filter(x => x.item_level === 'customer')
            .map(x => ({ id: x.id, name: this.normalizeName(x.item_name) }));

        // Subscribe to customer changes
        this.form.get('customer')?.valueChanges.subscribe(selectedCustomer => {
            this.regionsLoaded$.next(false);
            this.form.get('region')?.setValue(null);
            this.form.get('project')?.setValue(null);
            this.form.get('simulation')?.setValue(null);
            this.filteredProjects = [];
            this.filteredSimulations = [];
            if (selectedCustomer) {
                // Map regions with normalization
                const regions = this.nodes
                    .filter(x => x.item_level === 'region' && x.parent_id === selectedCustomer.id)
                    .map(x => ({ id: x.id, name: this.normalizeName(x.item_name), isRegion: true }));
                // Append only countries not already present by name (case-insensitive)
                const regionNames = regions.map(r => r.name);
                const uniqueCountries = this.countries.filter(c => !regionNames.includes(this.normalizeName(c.name)));

                let regionCountryList;
                if (regions.length > 0) {
                    regionCountryList = [...regions, ...uniqueCountries.map(c => ({ ...c, name: this.normalizeName(c.name), isCountry: true }))];
                } else {
                    regionCountryList = [...uniqueCountries.map(c => ({ ...c, name: this.normalizeName(c.name), isCountry: true }))];
                }


                this.regionsLength = regions.length;
                this.filteredRegions = regionCountryList;
                this.form.get('region')?.enable();
            } else {
                this.filteredRegions = [];
                this.form.get('region')?.disable();
            }
            this.regionsLoaded$.next(true);
        });

        // Subscribe to region changes
        this.form.get('region')?.valueChanges.subscribe(selectedRegion => {
            this.projectsLoaded$.next(false);
            this.form.get('project')?.setValue(null);
            this.form.get('simulation')?.setValue(null);
            this.filteredSimulations = [];
            if (selectedRegion) {
                this.filteredProjects = this.nodes
                    .filter(x => x.item_level === 'project' && x.parent_id === selectedRegion.id)
                    .map(x => ({ id: x.id, name: this.normalizeName(x.item_name) }));
                this.form.get('project')?.enable();
            } else {
                this.filteredProjects = [];
                this.form.get('project')?.disable();
            }
            this.projectsLoaded$.next(true);
        });

        // Subscribe to project changes
        this.form.get('project')?.valueChanges.subscribe(selectedProject => {
            this.simulationsLoaded$.next(false);
            this.form.get('simulation')?.setValue(null);
            if (selectedProject) {
                this.filteredSimulations = this.nodes
                    .filter(x => x.item_level === 'simulation' && x.parent_id === selectedProject.id)
                    .map(x => ({ id: x.id, name: this.normalizeName(x.item_name) }));
                this.form.get('simulation')?.enable();
            } else {
                this.filteredSimulations = [];
                this.form.get('simulation')?.disable();
            }
            this.simulationsLoaded$.next(true);
        });

        // Call getSimulation when a simulation is selected
        this.form.get('simulation')?.valueChanges.subscribe(selectedSimulation => {
            this.simulationNameDraft = null;
            this.simulationNameSource = null;
            if (selectedSimulation) {
                const preserveDetails = selectedSimulation.__isNew__ && this.form.get('visibility')?.enabled;
                this.form.get('notes')?.enable();
                this.form.get('visibility')?.enable();
                if (!preserveDetails) {
                    this.getSimulation(selectedSimulation);
                }
            } else {
                this.form.get('notes')?.disable();
                this.form.get('visibility')?.disable();
                this.form.get('notes')?.setValue(null);
                this.form.get('visibility')?.setValue(null);
            }
        });

        this.form.get('region')?.disable();
        this.form.get('project')?.disable();
        this.form.get('simulation')?.disable();
        this.form.get('notes')?.disable();
        this.form.get('visibility')?.disable();
    }

    private loadSelects() {
        if (this.userId && this.module) {
            this.treeService.getTreeByUserId(this.userId, this.module).subscribe(levels => {
                if (levels?.status?.success) {
                    this.nodes = levels.data;
                    const grouped = levels.data.reduce((acc, item) => {
                        if (!acc[item.item_level]) acc[item.item_level] = [];
                        acc[item.item_level].push({ id: item.id, name: item.item_name });
                        return acc;
                    }, {} as any);

                    this.users = grouped['user'] || [];
                    this.customers = grouped['customer'] || [];
                    this.regions = grouped['region'] || [];
                    this.projects = grouped['project'] || [];
                    this.simulations = grouped['simulation'] || [];

                    this.nodes = levels.data;
                    this.configureSelects();
                    this.preloadForm();
                }
            });
        }
    }

    private getSimulation(selected: any) {
        if (selected.id) {
            if (this.module == 'corrosionclas') {
                this.corrosionService.getSimulationByTreeId(selected.id).subscribe(simDB => {
                    if (simDB.status.success == true) {
                        this.form.get('notes')?.setValue(simDB.data.notes);
                        this.form.get('visibility')?.setValue(simDB.data.visibility);
                    } else {
                        this.form.get('notes')?.setValue(null);
                        this.form.get('visibility')?.setValue(null);
                    }
                });
            } else if (this.module == 'corrosionrate') {
                this.corrosionRateService.loadSimulation(selected.id).subscribe(simDB => {
                    this.form.get('notes')?.setValue(simDB.notes ?? null);
                    this.form.get('visibility')?.setValue(simDB.visibility ?? null);
                });
            } else if (this.module == 'slottedPipe') {
                this.slottedPipeService.loadSimulation(selected.id).subscribe(simulation => {
                    this.form.get('notes')?.setValue(simulation.tree_notes);
                    this.form.get('visibility')?.setValue(simulation.visibility ?? null);
                });
            } else if (this.module == 'perforatedPipe') {
                this.perforatedPipeService.loadSimulation(selected.id).subscribe(simulation => {
                    this.form.get('notes')?.setValue(simulation.tree_notes);
                    this.form.get('visibility')?.setValue(simulation.visibility ?? null);
                });
            } else if (this.module == 'matSelGuidelines') {
                this.matSelGuidelinesService.loadSimulation(selected.id).subscribe(simulation => {
                    this.form.get('notes')?.setValue(simulation.matSelGuideline.tree_notes);
                    this.form.get('visibility')?.setValue(simulation.matSelGuideline.visibility ?? null);
                });
            }
        } else {
            this.form.get('notes')?.setValue(null);
            this.form.get('visibility')?.setValue(this.visibilityOptions.find(v => v.description === SimulationVisibility.Colleagues));
        }
    }

    private loadCountries(): void {
        // Primero intentar obtener desde saveSimulationService
        let rawCountries = this.saveSimulationService.getCountries();

        // Si no hay países en saveSimulationService, obtener desde preSimulationService
        if (!rawCountries || rawCountries.length === 0) {
            rawCountries = this.preSimulationService.getCountries() || [];
            // Guardar en saveSimulationService para uso futuro
            if (rawCountries.length > 0) {
                this.saveSimulationService.setCountries(rawCountries);
            }
        }

        this.countries = rawCountries.map(x => this.createNewEntity(x.description));
    }

    private normalizeName(str: string): string {
        return this.saveSimulationService.normalizeName(str);
    }

    private createNewEntity(name: string) {
        return this.saveSimulationService.createNewEntity(name);
    }

    private initFormControlWithFilteredList(controlName: string, value: any, list: Entity[]): void {
        const normalized = this.normalizeName(value?.description || value?.name);
        let found = list.find(e => this.normalizeName(e.name) === normalized);
        if (found) {
            this.form.get(controlName)?.setValue(found);
        } else {
            const newEntity = this.createNewEntity(normalized);
            // Replace array to trigger Angular change detection
            if (list === this.filteredCustomers) {
                this.filteredCustomers = [...this.filteredCustomers, newEntity];
            } else if (list === this.filteredRegions) {
                this.filteredRegions = [...this.filteredRegions, newEntity];
            }
            this.form.get(controlName)?.setValue(newEntity);
        }
    }

    private findByName(list: Entity[], value: any): Entity | undefined {
        const normalized = this.normalizeName(value?.description || value?.name);
        return list.find(e => this.normalizeName(e.name) === normalized);
    }

    private mapFormValueToSaveSimulationForm(formValue: any): SaveSimulationForm {
        return {
            userId: this.userId ?? null,
            customer: {
                id: formValue.customer?.id ?? null,
                description: formValue.customer?.name ?? ''
            },
            region: {
                id: formValue.region?.id ?? null,
                description: formValue.region?.name ?? ''
            },
            project: {
                id: formValue.project?.id ?? null,
                description: formValue.project?.name ?? ''
            },
            simulation: {
                id: formValue.simulation?.id ?? null,
                description: formValue.simulation?.name ?? ''
            },
            visibility: formValue.visibility?.description ?? '',
            notes: formValue.notes ?? '',
            path: [formValue.customer?.name ?? '', formValue.region?.name ?? '', formValue.project?.name ?? '', formValue.simulation?.name ?? ''].filter(Boolean).join('-')
        };
    }
}
